import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { useNotify, usePermissions, useRecordContext, useRefresh } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";
import { apiErrorMessage } from "../shared/api-error";
import { MADRASA_ENROLLMENTS_EDIT } from "../shared/madrasa-permissions";

type EnrollmentRecord = {
  id: string;
  status: string;
  studentName: string;
  className: string;
  academicYearName: string;
  enrolledOn: string;
};

/** Ends an active enrollment. To change a student's class: withdraw here, then enroll again. */
export function WithdrawButton({ size = "small" }: { size?: "small" | "medium" }) {
  const record = useRecordContext<EnrollmentRecord>();
  const { permissions } = usePermissions();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [leftOn, setLeftOn] = useState("");
  const [busy, setBusy] = useState(false);

  if (
    !record ||
    record.status !== "ACTIVE" ||
    !hasPermission(permissions, MADRASA_ENROLLMENTS_EDIT)
  ) {
    return null;
  }

  const close = () => {
    if (!busy) {
      setOpen(false);
      setLeftOn("");
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await callApi({
        location: `madrasaEnrollments/${record.id}/withdraw`,
        method: "POST",
        data: leftOn ? { leftOn } : {},
      });
      notify("Enrollment withdrawn", { type: "success" });
      setOpen(false);
      setLeftOn("");
      refresh();
    } catch (error: unknown) {
      notify(apiErrorMessage(error, "Could not withdraw the enrollment"), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        color="warning"
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
      >
        Withdraw
      </Button>
      <Dialog
        open={open}
        onClose={close}
        onClick={(event) => event.stopPropagation()}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Withdraw {record.studentName}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This ends their place in {record.className} for {record.academicYearName}. To move them
            to another class, withdraw here and then enroll them in the new class. Fees for the
            month of their last day stay with {record.className}; the new class bills from the next
            month.
          </DialogContentText>
          <TextField
            label="Last day in class"
            type="date"
            value={leftOn}
            onChange={(event) => setLeftOn(event.target.value)}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            helperText={`Leave empty for today. Must be on or after ${record.enrolledOn}.`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} color="warning" variant="contained" disabled={busy}>
            {busy ? "Withdrawing…" : "Withdraw"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
